import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Text,
  useToast,
} from '@chakra-ui/react';
import { showToast } from '@diamond/shared/ui';
import { Close } from '@mui/icons-material';
import { useImperativeHandle, useRef, useState } from 'react';

interface CartVoucherHeaderProps {
  onSearch: (code: string) => void;
  isSearching: boolean;
  onClose: () => void;
  headerRef?: React.Ref<CartVoucherHeaderHandle>;
}

export interface CartVoucherHeaderHandle {
  resetSearchField: () => void;
  setInputValue: (value: string) => void;
}

export const CartVoucherHeader = ({
  onSearch,
  isSearching,
  onClose,
  headerRef,
}: CartVoucherHeaderProps) => {
  const toast = useToast();
  const [code, setCode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [voucherCode, setVoucherCode] = useState<string>('');

  useImperativeHandle(headerRef, () => ({
    resetSearchField: () => {
      setCode('');
      inputRef.current?.focus();
    },
    setInputValue: (value: string) => {
      setVoucherCode(value.toUpperCase());
    },
  }));

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const normalized = text.trim().toUpperCase();
      if (!normalized) {
        showToast(
          toast,
          'warning',
          'Kode voucher kosong - Silakan masukkan kode voucher',
          undefined,
          'bottom',
          3000
        );
        return;
      }
      setCode(normalized);
      inputRef.current?.focus();
    } catch {
      showToast(
        toast,
        'error',
        'Gagal menempel kode - Tidak dapat mengakses clipboard',
        undefined,
        'bottom',
        3000
      );
    }
  };

  const handleSearch = () => {
    const trimmed = code.trim();
    if (!trimmed) {
      showToast(
        toast,
        'warning',
        'Kode voucher kosong - Silakan masukkan kode voucher',
        undefined,
        'bottom',
        3000
      );
      return;
    }
    onSearch(trimmed);
  };

  const hasCode = code.trim().length > 0;

  return (
    <Flex bg="#00003D" flexDirection="column" py="4" px="4" gap="2">
      <Flex justify="space-between" align="center">
        <Text color="white" fontSize="20px" fontWeight="600">
          Voucher
        </Text>
        <IconButton
          onClick={onClose}
          colorScheme="white"
          aria-label="Tutup"
          variant="link"
          color="white"
          icon={<Close />}
          minW="auto"
          h="auto"
          p="1"
        />
      </Flex>

      <HStack spacing="2" flex="1">
        <Box flex="2" position="relative">
          <Input
            ref={inputRef}
            placeholder="Masukkan kode voucher"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
            }}
            bg="white"
            border="none"
            borderRadius="8px"
            fontSize="14px"
            h="40px"
            pr="70px"
            _placeholder={{ color: 'gray.400' }}
          />
          <Button
            zIndex="5"
            position="absolute"
            right="8px"
            top="50%"
            transform="translateY(-50%)"
            variant="link"
            color="#0000A3"
            fontSize="14px"
            fontWeight="600"
            onClick={handlePaste}
            h="auto"
            minW="auto"
            p="0"
          >
            Tempel
          </Button>
        </Box>

        {hasCode ? (
          <Box
            bg="linear-gradient(58deg, rgba(255, 30, 30, 1) 18%, rgba(36, 81, 206, 1) 71%, rgba(90, 200, 255, 1) 100%)"
            p="1px"
            borderRadius="4px"
          >
            <Button
              variant="solid"
              bg="#0000A3"
              color="white"
              fontSize="14px"
              fontWeight="600"
              h="40px"
              px="6"
              borderRadius="3px"
              onClick={handleSearch}
              type="button"
              isDisabled={isSearching}
              isLoading={isSearching}
              _hover={{
                bg: '#0000A3',
                opacity: 0.9,
              }}
              w="100%"
            >
              Gunakan
            </Button>
          </Box>
        ) : (
          <Button
            colorScheme="gray"
            variant="solid"
            bg="gray.400"
            color="gray.600"
            fontSize="14px"
            fontWeight="600"
            h="40px"
            px="6"
            borderRadius="8px"
            onClick={handleSearch}
            type="button"
            isDisabled={!hasCode || isSearching}
            isLoading={isSearching}
          >
            Gunakan
          </Button>
        )}
      </HStack>
    </Flex>
  );
};

export default CartVoucherHeader;
